import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, ArrowRight, CheckCircle, Shield, Clock, Sparkles, Users, Calendar } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const HERO_IMAGE = "https://images.unsplash.com/photo-1721523257891-0d2df324bcfc?auto=format&fit=crop&w=2000&q=80";

const trustStats = [
  { icon: Calendar, label: '19+ Years', sublabel: 'Experience' },
  { icon: Shield, label: 'Fully', sublabel: 'Insured' },
  { icon: Users, label: 'Northeast FL', sublabel: 'Service Area' },
  { icon: Clock, label: 'Same Day', sublabel: 'Response' },
];

const services = [
  {
    title: 'Kitchen Remodeling',
    description: 'Transform your kitchen into a stunning culinary space with custom cabinetry, premium countertops, and thoughtful design.',
    image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=800&q=80',
  },
  {
    title: 'Bathroom Remodeling',
    description: 'Create your personal spa retreat with elegant fixtures, quality tile work, and luxurious finishes.',
    image: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&w=800&q=80',
  },
  {
    title: 'Interior Renovations',
    description: 'Revitalize your living spaces with expert interior renovations that enhance both beauty and functionality.',
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=800&q=80',
  },
];

const projects = [
  {
    title: 'Coastal Kitchen Transformation',
    location: 'St. Augustine, FL',
    image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=800&q=80',
  },
  {
    title: 'Modern Master Bath',
    location: 'Ponte Vedra, FL',
    image: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&w=800&q=80',
  },
  {
    title: 'Open Concept Living',
    location: 'Jacksonville, FL',
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=800&q=80',
  },
  {
    title: 'Elegant Flooring Update',
    location: 'Nocatee, FL',
    image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=800&q=80',
  },
];

const jaxStandards = [
  {
    icon: Sparkles,
    title: 'Pristine Jobsites',
    description: 'Daily cleanup, dust barriers, and protection for your home throughout the project.',
  },
  {
    icon: CheckCircle,
    title: 'Transparent Process',
    description: 'Clear scope documentation, honest timelines, and no surprise change orders.',
  },
  {
    icon: Users,
    title: 'High-Touch Communication',
    description: 'Daily or weekly updates, responsive to your questions, always accessible.',
  },
  {
    icon: Shield,
    title: 'Premium Craftsmanship',
    description: 'Attention to every detail with quality materials and expert execution.',
  },
];

const processSteps = [
  { number: '01', title: 'Inquiry', description: 'Quick phone consultation' },
  { number: '02', title: 'Site Visit', description: 'Scope alignment meeting' },
  { number: '03', title: 'Estimate', description: 'Detailed proposal & timeline' },
  { number: '04', title: 'Pre-Start', description: 'Materials & protection plan' },
  { number: '05', title: 'Build', description: 'Expert execution & updates' },
  { number: '06', title: 'Closeout', description: 'Final walkthrough & punch list' },
];

const testimonials = [
  {
    quote: "The attention to detail was exceptional. Our kitchen looks like it belongs in a magazine.",
    author: "Sarah M.",
    location: "St. Augustine",
    placeholder: true,
  },
  {
    quote: "Professional from start to finish. They kept us informed every step of the way.",
    author: "Michael R.",
    location: "Jacksonville",
    placeholder: true,
  },
  {
    quote: "Clean, efficient, and the results exceeded our expectations. Highly recommend!",
    author: "Jennifer L.",
    location: "Ponte Vedra",
    placeholder: true,
  },
];

const faqs = [
  {
    question: 'How long does a typical kitchen remodel take?',
    answer: 'Timeline varies based on scope, but most kitchen remodels take 4-8 weeks. We provide a detailed schedule during the estimate phase.',
  },
  {
    question: 'Do you provide free estimates?',
    answer: 'Yes, we offer complimentary consultations and detailed estimates for all projects.',
  },
  {
    question: 'What areas do you serve?',
    answer: 'We serve St. Augustine, Jacksonville, Ponte Vedra, Nocatee, St. Johns, and Palm Coast in Northeast Florida.',
  },
];

export const Home = () => {
  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Luxury kitchen remodel"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <div className="w-16 h-px bg-[#C6A87C] mb-8" />
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-tight">
              Luxury Living,<br />Built to Last
            </h1>
            <p className="text-white/80 text-lg md:text-xl mb-10 leading-relaxed">
              Precision remodeling with white-glove service for discerning homeowners in Northeast Florida.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="tel:9048636673"
                data-testid="hero-call-btn"
                className="inline-flex items-center justify-center gap-3 bg-[#C6A87C] text-white px-8 py-4 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
              >
                <Phone className="w-4 h-4" />
                Call Now
              </a>
              <Link
                to="/contact"
                data-testid="hero-estimate-btn"
                className="inline-flex items-center justify-center gap-3 bg-white text-[#0A0A0A] px-8 py-4 text-xs font-semibold uppercase tracking-widest hover:bg-gray-100 transition-colors"
              >
                Request Estimate
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-[#0A0A0A] py-8" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {trustStats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-4"
              >
                <stat.icon className="w-6 h-6 text-[#C6A87C]" />
                <div>
                  <p className="text-white font-semibold">{stat.label}</p>
                  <p className="text-gray-400 text-sm">{stat.sublabel}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 md:py-32 bg-[#F9F9F7]" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="w-16 h-px bg-[#C6A87C] mx-auto mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl mb-4">Our Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive remodeling services with meticulous attention to quality and craftsmanship.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="group bg-white overflow-hidden"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-8">
                  <h3 className="font-heading text-xl mb-3">{service.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{service.description}</p>
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#0A0A0A] hover:text-[#C6A87C] transition-colors"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              data-testid="view-all-services-btn"
              className="inline-flex items-center justify-center gap-3 bg-[#0A0A0A] text-white px-8 py-4 text-xs font-semibold uppercase tracking-widest hover:bg-[#C6A87C] transition-colors"
            >
              View All Services
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-24 md:py-32" data-testid="featured-projects">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6"
          >
            <div>
              <div className="w-16 h-px bg-[#C6A87C] mb-6" />
              <h2 className="font-heading text-3xl md:text-4xl mb-4">Featured Projects</h2>
              <p className="text-gray-600 max-w-lg">
                A selection of our recent transformations across Northeast Florida.
              </p>
            </div>
            <Link
              to="/projects"
              data-testid="view-portfolio-btn"
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#0A0A0A] hover:text-[#C6A87C] transition-colors"
            >
              View Portfolio
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {projects.map((project, index) => (
              <motion.div
                key={project.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative aspect-[4/5] overflow-hidden cursor-pointer"
              >
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                  <h3 className="font-heading text-white text-lg mb-1">{project.title}</h3>
                  <p className="text-white/70 text-sm">{project.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Jax Standard */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="jax-standard">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="w-16 h-px bg-[#C6A87C] mx-auto mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">The Jax Standard</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Our commitment to excellence goes beyond beautiful results. It's about respect, communication, and precision in every detail.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {jaxStandards.map((standard, index) => (
              <motion.div
                key={standard.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center border border-[#C6A87C]/30 rounded-sm">
                  <standard.icon className="w-8 h-8 text-[#C6A87C]" />
                </div>
                <h3 className="font-heading text-white text-xl mb-3">{standard.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{standard.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Teaser */}
      <section className="py-24 md:py-32" data-testid="process-teaser">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="w-16 h-px bg-[#C6A87C] mx-auto mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl mb-4">Our Process</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              A refined approach that ensures clarity, quality, and peace of mind from start to finish.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <span className="font-heading text-5xl text-[#C6A87C]/20 block mb-2">{step.number}</span>
                <h3 className="font-heading text-lg mb-1">{step.title}</h3>
                <p className="text-gray-500 text-xs">{step.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/process"
              data-testid="learn-process-btn"
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#0A0A0A] hover:text-[#C6A87C] transition-colors"
            >
              Learn About Our Process
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews Teaser */}
      <section className="py-24 md:py-32 bg-[#F9F9F7]" data-testid="reviews-teaser">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="w-16 h-px bg-[#C6A87C] mx-auto mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl mb-4">What Our Clients Say</h2>
            <p className="text-gray-500 text-sm">(Placeholder testimonials for demonstration)</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.author}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="bg-white p-8"
              >
                <span className="font-heading text-6xl text-[#C6A87C]/20 leading-none block mb-4">"</span>
                <p className="text-gray-700 mb-6 leading-relaxed">{testimonial.quote}</p>
                <div>
                  <p className="font-semibold text-sm">{testimonial.author}</p>
                  <p className="text-gray-500 text-xs">{testimonial.location}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/reviews"
              data-testid="view-reviews-btn"
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#0A0A0A] hover:text-[#C6A87C] transition-colors"
            >
              View All Reviews
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Teaser */}
      <section className="py-24 md:py-32" data-testid="faq-teaser">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="w-16 h-px bg-[#C6A87C] mx-auto mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl mb-4">Frequently Asked</h2>
          </motion.div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border border-gray-200 px-6"
              >
                <AccordionTrigger className="text-left font-medium py-5 hover:no-underline hover:text-[#C6A87C]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="text-center mt-10">
            <Link
              to="/faq"
              data-testid="view-all-faq-btn"
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#0A0A0A] hover:text-[#C6A87C] transition-colors"
            >
              View All FAQs
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-px bg-[#C6A87C] mx-auto mb-8" />
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mb-6">
              Ready to Transform Your Space?
            </h2>
            <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
              Let's discuss your vision. Schedule a consultation and discover the Jax Remodeling difference.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="tel:9048636673"
                data-testid="cta-call-btn"
                className="inline-flex items-center justify-center gap-3 bg-[#C6A87C] text-white px-8 py-4 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
              >
                <Phone className="w-4 h-4" />
                (904) 863-6673
              </a>
              <Link
                to="/contact"
                data-testid="cta-estimate-btn"
                className="inline-flex items-center justify-center gap-3 bg-white text-[#0A0A0A] px-8 py-4 text-xs font-semibold uppercase tracking-widest hover:bg-gray-100 transition-colors"
              >
                Request Free Estimate
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};
